import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { BarChart3, FileCheck2, KeyRound, ShieldCheck, Sparkles } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ceo-operacional/artifacts/2hvzdama_LogocomSlogan-fundoescuro.png";

const CAPABILITIES = [
  { icon: BarChart3, title: "Gestão comercial", text: "Pipeline, propostas e indicadores em um único ambiente." },
  { icon: FileCheck2, title: "Operação técnica", text: "Documentos, treinamentos e evidências com rastreabilidade." },
  { icon: ShieldCheck, title: "Acesso protegido", text: "Perfis, auditoria e autenticação em dois fatores para administração." },
];

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [mode, setMode] = useState("admin");
  const [pwd, setPwd] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [totpRequired, setTotpRequired] = useState(false);
  const [totpToken, setTotpToken] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let payload;
      if (mode === "colaborador") {
        payload = { email, password: pwd };
      } else {
        payload = { password: pwd };
        if (totpRequired) {
          if (useRecovery) payload.recovery_code = recoveryCode;
          else payload.totp_token = totpToken;
        }
      }
      const data = await login(payload);
      if (data?.totp_required) {
        setTotpRequired(true);
        toast.info("Informe o código do Google Authenticator");
        return;
      }
      toast.success("Bem-vindo");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha no login");
    } finally { setLoading(false); }
  };

  const resetTotp = () => {
    setTotpRequired(false);
    setTotpToken("");
    setRecoveryCode("");
    setUseRecovery(false);
  };

  return (
    <div className="min-h-screen bg-background lg:grid lg:grid-cols-[1.08fr_0.92fr]">
      <section className="relative hidden min-h-screen overflow-hidden border-r border-white/[0.06] bg-[hsl(154_40%_4%)] p-10 text-white lg:flex lg:flex-col xl:p-14">
        <div className="pointer-events-none absolute -left-20 -top-24 size-[32rem] rounded-full bg-primary/15 blur-[100px]" />
        <div className="pointer-events-none absolute -bottom-32 right-[-10rem] size-[34rem] rounded-full bg-emerald-500/10 blur-[110px]" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.035]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.9) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.9) 1px, transparent 1px)", backgroundSize: "48px 48px" }} />

        <div className="relative">
          <img src={LOGO_URL} alt="DEMoraes Consultoria" className="h-20 w-auto object-contain object-left xl:h-24" />
          <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.15em] text-emerald-200">
            <Sparkles className="size-3" /> Centro de Eficiência Operacional
          </div>
        </div>

        <div className="relative my-auto max-w-2xl py-12">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-white/40">CEO · DEMoraes Consultoria</div>
          <h1 className="mt-4 max-w-xl text-4xl font-semibold leading-[1.08] tracking-[-0.045em] xl:text-5xl">
            Gestão técnica e comercial em um único ambiente operacional.
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-white/55">
            Controle propostas, clientes, documentos e treinamentos com uma interface criada para decisão rápida, rastreabilidade e segurança.
          </p>

          <div className="mt-10 grid max-w-xl gap-3">
            {CAPABILITIES.map(({ icon: Icon, title, text }) => (
              <div key={title} className="flex items-start gap-4 rounded-2xl border border-white/[0.07] bg-white/[0.035] p-4 backdrop-blur-sm">
                <div className="grid size-10 shrink-0 place-items-center rounded-xl border border-primary/15 bg-primary/10 text-emerald-300">
                  <Icon className="size-4.5" />
                </div>
                <div>
                  <div className="text-sm font-medium text-white/90">{title}</div>
                  <div className="mt-0.5 text-xs leading-relaxed text-white/45">{text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative text-[10px] font-mono uppercase tracking-[0.14em] text-white/30">
          DEMoraes Consultoria · Ambiente interno protegido
        </div>
      </section>

      <section className="relative flex min-h-screen items-center justify-center px-4 py-8 sm:px-8 lg:px-12">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-primary/[0.055] to-transparent" />
        <div className="relative w-full max-w-md animate-fade-in" data-testid="login-form">
          <div className="mb-7 text-center lg:text-left">
            <img src={LOGO_URL} alt="DEMoraes" className="mx-auto mb-5 h-16 object-contain lg:hidden" />
            <div className="label-tech">Acesso ao sistema</div>
            <h2 className="mt-2 text-2xl font-semibold tracking-[-0.04em] sm:text-3xl">Bem-vindo de volta</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Entre com suas credenciais para acessar o Centro de Eficiência Operacional.</p>
          </div>

          <div className="rounded-2xl border border-border/80 bg-card/90 p-5 shadow-[0_1px_2px_hsl(var(--foreground)/0.04),0_24px_70px_hsl(var(--foreground)/0.07)] backdrop-blur-sm sm:p-7">
            {!totpRequired && (
              <div className="mb-5 grid grid-cols-2 gap-1 rounded-xl border border-border/70 bg-muted/35 p-1" data-testid="login-mode-toggle">
                <button
                  type="button"
                  aria-pressed={mode === "admin"}
                  onClick={() => { setMode("admin"); resetTotp(); }}
                  className={`rounded-lg px-3 py-2.5 text-[10px] font-mono uppercase tracking-[0.11em] transition-all ${mode === "admin" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid="login-mode-admin"
                >
                  Administrador
                </button>
                <button
                  type="button"
                  aria-pressed={mode === "colaborador"}
                  onClick={() => { setMode("colaborador"); resetTotp(); }}
                  className={`rounded-lg px-3 py-2.5 text-[10px] font-mono uppercase tracking-[0.11em] transition-all ${mode === "colaborador" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid="login-mode-colaborador"
                >
                  Colaborador
                </button>
              </div>
            )}

            <form onSubmit={submit} className="space-y-4">
              {!totpRequired && mode === "colaborador" && (
                <div className="space-y-1.5">
                  <Label className="label-tech">E-mail</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" autoFocus required placeholder="nome@empresa.com.br" />
                </div>
              )}
              {!totpRequired && (
                <div className="space-y-1.5">
                  <Label className="label-tech">Senha de acesso</Label>
                  <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="login-password-input" autoFocus={mode === "admin"} placeholder="••••••••••••" />
                </div>
              )}

              {totpRequired && !useRecovery && (
                <div className="space-y-4" data-testid="login-totp-step">
                  <div className="flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/[0.055] p-3.5">
                    <div className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><ShieldCheck className="size-4" /></div>
                    <div className="text-xs leading-relaxed text-muted-foreground">
                      Abra o <span className="font-semibold text-foreground">Google Authenticator</span> e informe o código de 6 dígitos de <span className="font-mono text-foreground">CEO DEMoraes · admin</span>.
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="label-tech">Código 2FA</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      placeholder="000000"
                      value={totpToken}
                      onChange={(e) => setTotpToken(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className="h-12 text-center font-mono text-xl tracking-[0.38em]"
                      data-testid="login-totp-input"
                      autoFocus
                    />
                  </div>
                  <button type="button" onClick={() => setUseRecovery(true)} className="block w-full text-center text-xs text-muted-foreground transition hover:text-primary" data-testid="login-use-recovery-btn">
                    Perdi meu dispositivo — usar código de recuperação
                  </button>
                </div>
              )}

              {totpRequired && useRecovery && (
                <div className="space-y-4" data-testid="login-recovery-step">
                  <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.055] p-3.5">
                    <div className="grid size-8 shrink-0 place-items-center rounded-lg bg-amber-500/10 text-amber-500"><KeyRound className="size-4" /></div>
                    <div className="text-xs leading-relaxed text-muted-foreground">
                      Use um dos <span className="font-semibold text-foreground">códigos de recuperação</span> de uso único salvos na ativação do 2FA.
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="label-tech">Código de recuperação</Label>
                    <Input
                      type="text"
                      placeholder="XXXX-XXXX-XXXX"
                      value={recoveryCode}
                      onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                      className="text-center font-mono tracking-wider"
                      data-testid="login-recovery-input"
                      autoFocus
                    />
                  </div>
                  <button type="button" onClick={() => setUseRecovery(false)} className="block w-full text-center text-xs text-muted-foreground transition hover:text-primary" data-testid="login-use-totp-btn">
                    Voltar ao código do Authenticator
                  </button>
                </div>
              )}

              <Button
                type="submit"
                disabled={
                  loading ||
                  (!totpRequired && !pwd) ||
                  (!totpRequired && mode === "colaborador" && !email) ||
                  (totpRequired && !useRecovery && totpToken.length !== 6) ||
                  (totpRequired && useRecovery && recoveryCode.length < 12)
                }
                className="mt-1 w-full"
                data-testid="login-submit-btn"
              >
                {loading ? "Entrando..." : totpRequired ? "Verificar e entrar" : "Entrar"}
              </Button>

              {totpRequired && (
                <button type="button" onClick={resetTotp} className="block w-full text-[10px] font-mono uppercase tracking-[0.11em] text-muted-foreground transition hover:text-foreground" data-testid="login-cancel-totp-btn">
                  Cancelar verificação
                </button>
              )}
            </form>

            {!totpRequired && mode === "admin" && (
              <div className="mt-5 text-center">
                <Link to="/recover" className="text-xs text-muted-foreground transition hover:text-primary" data-testid="forgot-password-link">Esqueci minha senha</Link>
              </div>
            )}
          </div>

          <div className="mt-5 text-center text-[9px] font-mono uppercase tracking-[0.14em] text-muted-foreground/70">
            DEMoraes Consultoria · acesso seguro
          </div>
        </div>
      </section>
    </div>
  );
}
